// 등급 점수에서 만족도를 뺀다 — 2026-10-26 대표 결정(B안).
//
// 기본 점수 = 성과 40 + 꾸준함 10 + 참여 20 (70점 만점) + 가감점. 만족도(검수 한 번에 통과 비율)는 기록만 하고
// 점수에 넣지 않는다. 컷도 만족도 평균 몫(0.3 × 97.8 = 29.34)만큼 내렸다 → 다이아 40.9 · 플래 25.1 · 골드 14.2.
// DB: creator_tier_config.satisfaction_off (마이그레이션 20261123100000) — 계산 시점이 아래 날짜 0시(KST) 이후면 적용.
//
// 크리에이터 화면은 creator_tier_config 를 읽을 수 없어서(어드민 전용 행) tierWindowCopy.js 처럼 **빌드 상수**로
// 서버 설정과 맞춘다. 🔒 날짜·가중치는 마이그레이션 값과 같아야 한다 — tierSatisfaction.test.js 가 대조한다.
//
// 이 날짜 전에 만든 일별·월 기록(overall_score)은 만족도가 들어간 100점 기준이다. 그래프·비교에서 새 기록과 섞이면
// 하루 사이에 29점쯤 떨어진 것처럼 보이므로, 화면은 옛 기록에서 만족도 몫을 빼서 같은 기준으로 그린다
// (DB 기록은 그대로 — 그때 확정한 등급의 근거라 고치지 않는다).

/** 만족도를 점수에 넣는가 — false = 2026-10-27 부터의 계산 */
export const TIER_SATISFACTION_IN_SCORE = false;

/** 만족도 제외가 시작된 계산 시점(KST 날짜). 월 기록은 `month`(그 달 1일 = 확정 시점)로 비교한다 */
export const TIER_SATISFACTION_OFF_FROM = "2026-10-27";

/** 예전 기본 점수에서 만족도에 붙던 가중치 */
export const SATISFACTION_WEIGHT = 0.3;

/** 기본 점수 만점(가감점 제외) — 성과 40 + 꾸준함 10 + 참여 20 */
export const TIER_BASE_SCORE_MAX = 70;

/** 만족도 제외 기간의 등급 컷(70점 기준 + 가감점) — DB satisfaction_off.cut_a/b/c 와 같다 */
export const SATISFACTION_OFF_CUTS = Object.freeze({ 다이아몬드: 40.9, 플래티넘: 25.1, 골드: 14.2 });

/** 그 기록(일별 computed_date · 월 month · 통계 행의 날짜)이 만족도 제외 전 계산인가 */
export function isBeforeSatisfactionOff(dateStr) {
  if (!dateStr) return false;
  return String(dateStr).slice(0, 10) < TIER_SATISFACTION_OFF_FROM;
}

/** 기록의 날짜 — 일별은 computed_date, 월은 month */
export function tierRowDate(row) {
  return row?.computed_date ?? row?.month ?? null;
}

/**
 * 화면에 그릴 종합 점수 — 만족도 제외 전 기록이면 만족도 몫을 뺀다(새 기준과 같은 눈금).
 * 만족도 값이 없는 옛 기록은 평균 몫(29.34)을 뺀다. 점수가 없으면 null.
 */
export function displayOverallScore(row) {
  const v = row?.overall_score;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (TIER_SATISFACTION_IN_SCORE || !isBeforeSatisfactionOff(tierRowDate(row))) return n;
  const sat = row?.satisfaction_score;
  const satNum = sat === null || sat === undefined || sat === "" ? 97.8 : Number(sat);
  return Math.round((n - SATISFACTION_WEIGHT * (Number.isFinite(satNum) ? satNum : 97.8)) * 100) / 100;
}

/** 두 기록이 같은 점수 눈금인가(둘 다 만족도 제외 전이거나 둘 다 뒤) — 컷 비교·게이지에 쓴다 */
export function sameScoreScale(dateA, dateB) {
  return isBeforeSatisfactionOff(dateA) === isBeforeSatisfactionOff(dateB);
}

// ── 「자료 방식」 70점 만점 — 12월 등급(11월 활동)부터 (대표 결정 2026-10-26 · 마이그레이션 20261124100000) ──────────
// 성과 = 그 달 유효 조회수 ÷ 1,000만 × 40 · 참여 = 그 달 업로드 ÷ 100편 × 20 · 꾸준함 = 7일 칸마다 20편 대비 평균 × 10.
// 컷 66.5 / 49 / 28(실버 목표 14 — 미달도 실버) · 마스터 = 다이아 컷 이상이면서 정식 등급 전체 상위 20%.
// 서버 판정은 creator_tier_config.linear_from(2026-11-01 00:00 KST) — 계산 시점이 그 **뒤**면 적용.
// 일별 기록은 2026-11-01(07:00 계산)부터, 월 기록은 2026-12-01 확정(11월 활동)부터 이 방식이다.
// 🔒 값은 마이그레이션 linear_params 와 같아야 한다 — tierSatisfaction.test.js 가 대조한다.

/** 자료 방식이 시작된 일별 계산 날짜(KST) */
export const TIER_LINEAR_FROM = "2026-11-01";
/** 자료 방식으로 확정되는 첫 월 기록(month) — 12/1 확정 = 11월 활동 */
export const TIER_LINEAR_FIRST_MONTH = "2026-12-01";

/** 자료 방식 등급 컷(70점 기준 + 가감점) */
export const LINEAR_CUTS = Object.freeze({ 다이아몬드: 66.5, 플래티넘: 49, 골드: 28 });
/** 실버 목표선(안내용 — 미달도 실버) */
export const LINEAR_SILVER_TARGET = 14;
/** 만점 조건 */
export const LINEAR_FULL = Object.freeze({ views: 10000000, uploads: 100, weekly: 20 });
/** 배점 */
export const LINEAR_WEIGHTS = Object.freeze({ performance: 40, participation: 20, consistency: 10 });

/** 그 기록이 자료 방식 계산인가 — 일별 computed_date ≥ 11/1 · 월 month ≥ 12/1 · 통계 행도 같은 규칙 */
export function isLinearRow(row) {
  if (!row) return false;
  if (row.computed_date) return String(row.computed_date).slice(0, 10) >= TIER_LINEAR_FROM;
  if (row.month) return String(row.month).slice(0, 10) >= TIER_LINEAR_FIRST_MONTH;
  return false;
}

/** 오늘(KST)이 자료 방식 기간인가 — 기록 없이 그리는 안내 문구(루키 카드·안내 팝오버)가 쓴다 */
export function isLinearNow(now = new Date()) {
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return kst >= TIER_LINEAR_FROM;
}

/** 그 기록의 고정 컷(다이아·플래·골드) — 자료 방식이면 LINEAR_CUTS, 아니면 만족도 제외 컷 */
export function fixedCutsForRow(row) {
  return isLinearRow(row) ? LINEAR_CUTS : SATISFACTION_OFF_CUTS;
}

// ── 두 방식 병행 — 11월 활동(12월 등급)은 「지금 방식」·「새 방식」 중 높은 등급 (대표 결정 2026-10-28 · 마이그레이션 20261204100000) ──
// 서버 판정은 creator_tier_config.linear_params.dual_until(2026-12-01 00:00 KST) — 자료 방식이면서 계산 시점이 그때까지(포함)면
// 두 방식으로 각각 등급을 매겨 높은 쪽을 band 에 적는다. 점수 칸은 새 방식 그대로, 지금 방식 점수·등급은 alt_overall_score · alt_band.
// 일별 기록은 2026-11-01 ~ 11-30, 월 기록은 2026-12-01 확정(11월 활동) 한 번.
// 🔒 날짜는 마이그레이션 dual_until 과 같아야 한다 — tierSatisfaction.test.js 가 대조한다.

/** 두 방식을 같이 쓰는 마지막 일별 계산 날짜(KST) — 12/1 07:00 계산부터는 새 방식만 */
export const TIER_DUAL_LAST_DAY = "2026-11-30";

const DUAL_BAND_NAMES = Object.freeze({ S: "마스터", A: "다이아몬드", B: "플래티넘", C: "골드", D: "실버" });
const DUAL_TIER_ORDER = Object.freeze(["실버", "골드", "플래티넘", "다이아몬드", "마스터"]);

/** 새 방식 점수만으로 본 등급(마스터는 순위라 점수로 알 수 없어 다이아몬드로 본다) */
function linearTierFromScore(score) {
  if (score >= LINEAR_CUTS.다이아몬드) return "다이아몬드";
  if (score >= LINEAR_CUTS.플래티넘) return "플래티넘";
  if (score >= LINEAR_CUTS.골드) return "골드";
  return "실버";
}

/**
 * 그 기록이 두 방식 병행 기록이면 두 방식의 점수·등급을 돌려준다(아니면 null).
 * newTier 는 최종 등급이 지금 방식에서 왔을 때 새 방식 점수로 본 등급, 아니면 최종 등급 그대로.
 */
export function dualTierInfo(row) {
  if (!row || !row.alt_band || !DUAL_BAND_NAMES[row.alt_band]) return null;
  const newScore = Number(row.overall_score);
  const oldScore = Number(row.alt_overall_score);
  if (!Number.isFinite(newScore) || !Number.isFinite(oldScore)) return null;
  const finalTier = DUAL_BAND_NAMES[row.band] ?? null;
  const oldTier = DUAL_BAND_NAMES[row.alt_band];
  // 최종 등급이 지금 방식에서 왔는가 — 새 방식 점수로는 그 등급에 못 미칠 때(마스터는 다이아 컷 미만일 때)
  const rank = (name) => DUAL_TIER_ORDER.indexOf(name);
  const fromOld = row.band === row.alt_band && (
    finalTier === "마스터"
      ? newScore < LINEAR_CUTS.다이아몬드
      : rank(linearTierFromScore(newScore)) < rank(finalTier)
  );
  const newTier = fromOld ? linearTierFromScore(newScore) : finalTier;
  return { finalTier, newScore, newTier, oldScore, oldTier, fromOld };
}

/** 오늘(KST)이 두 방식 병행 기간인가 — 안내 문구용 */
export function isDualNow(now = new Date()) {
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return kst >= TIER_LINEAR_FROM && kst <= TIER_DUAL_LAST_DAY;
}
